import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, PageHero, Eyebrow, EASE } from "@/components/shared";
import { CTABand, FoundingOfferCard } from "@/pages/Home";
import { API } from "@/lib/api";
import { IMAGES } from "@/lib/content";

const CATEGORIES = ["All", "Hospitality", "Luxury Villas", "Fine Dining", "Marine & Services", "Professional Services"];

export default function CaseStudies() {
  const [cases, setCases] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    API.get("/case-studies")
      .then(({ data }) => setCases(data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const filtered = useMemo(() => (filter === "All" ? cases : cases.filter((c) => c.category === filter)), [cases, filter]);

  return (
    <main data-testid="case-studies-page">
      <PageHero
        eyebrow="Case Studies"
        title="Our work across Antigua & Barbuda."
        lead="Every engagement starts with a free conversation — and ends with numbers we are happy to publish."
        image={IMAGES.resort}
      />

      {loaded && cases.length === 0 ? (
        <section className="pb-24 lg:pb-32" data-testid="first-projects-section">
          <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
            <Reveal><Eyebrow>Our first projects</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-3xl tracking-tight text-ivory sm:text-4xl" style={{ lineHeight: 1.15 }}>
                Case studies coming soon.
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-5 text-base font-light leading-relaxed text-slate-300 sm:text-lg">
                We are taking our first founding clients now. Yours could be the first story we tell — real numbers,
                real outcomes, shared with your permission.
              </p>
            </Reveal>
            <Reveal delay={0.22}>
              <FoundingOfferCard />
            </Reveal>
          </div>
        </section>
      ) : (
        <section className="pb-24" data-testid="case-filter-section">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal>
              <div className="flex flex-wrap gap-2.5" data-testid="case-filters">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setFilter(c)}
                    data-testid={`case-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`rounded-full border px-5 py-2 text-xs font-medium transition-colors duration-300 ${filter === c ? "border-gold bg-gold/15 text-gold-glow" : "border-slate-600 text-slate-300 hover:border-gold/50"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </Reveal>

            <motion.div layout className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence mode="popLayout">
                {filtered.map((c) => (
                  <motion.article
                    layout
                    key={c.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="gold-ring-card group overflow-hidden"
                    data-testid={`case-study-${c.slug}`}
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img src={c.image} alt={c.name} className="h-full w-full object-cover duotone transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-surface via-transparent to-transparent" />
                      <span className="absolute left-5 top-5 rounded-full border border-gold/30 bg-navy-deep/80 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gold-glow backdrop-blur-md">
                        {c.category}
                      </span>
                    </div>
                    <div className="p-7">
                      <div className="flex items-baseline gap-3">
                        <p className="font-serif text-4xl text-gold-glow">{c.metric}</p>
                        <p className="text-xs uppercase tracking-widest text-slate-400">{c.metric_label}</p>
                      </div>
                      <h3 className="mt-5 font-serif text-xl text-ivory">{c.name}</h3>
                      <p className="mt-2 text-sm font-light text-slate-400">{c.title}</p>
                      <div className="mt-5 border-t border-gold/10 pt-5">
                        <p className="text-sm font-light italic leading-relaxed text-slate-300">“{c.quote}”</p>
                        <p className="mt-3 text-xs text-gold/70">{c.author}</p>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        </section>
      )}

      <CTABand
        title="Your business could be our first case study."
        text="Start with a free consultation — we will show you exactly where the opportunity sits."
      />
    </main>
  );
}
